import React, { useRef, useEffect, useState } from 'react';
import { useUpdateEffect } from 'ahooks';
import { useLocation } from '@remix-run/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { Skeleton } from '@rocco/components/skeleton';
import { buildPaginationUrl } from '@modules-rocco/pagination/util/build-pagination-url';
import { NoMatchesFound } from '@rocco/ui/no-matches/components/NoMatchesFound';
import { useCategoryTilesTracking } from '~/ui-rocco/hooks/use-category-tiles-tracking';
// Remove incorrect import path
// import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import type { DirectoryItemAdmin } from '~/modules/directory/type';
import { mapDirectoryItemAdminToTile } from '~/modules-rocco/admin/data-mapper/directory-item-admin-to-tile';
import { disableSliderAtom } from '@rocco/states/disableSliderAtom';
import type { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import type { useFetcher } from '@remix-run/react';
import type { FilterTabViewModel } from '@rocco/ui/filter-sort/view-models/filter-tab';

// Updated imports for extracted components
import { SearchPageGridHeader } from './SearchPageGridHeader';
import { SearchPageFilterTabs } from './SearchPageFilterTabs';
import { MobileSearchResultsAll } from './MobileSearchResultsAll';
import { SearchPageTile } from './SearchPageTile';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

type SearchResultPageTilesGridProps = ReturnType<
    typeof useCategoryFilterSort
> & {
    fetcherState: ReturnType<typeof useFetcher>['state'];
    isLoading: boolean;
};

/**
 * Renders the main grid of search results, including pagination and loading states.
 * Handles both desktop and mobile layouts, including the 'All' results view for mobile.
 */
export const SearchResultPageTilesGrid: React.FC<
    SearchResultPageTilesGridProps
> = (props) => {
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const results = pageDataFromAtom?.results ?? [];
    const pagination = pageDataFromAtom?.pagination ?? {
        page: 1,
        totalPages: 1,
        totalCount: 0,
    };
    const appDisplayMode = pageDataFromAtom?.appDisplayMode;
    const filterTabs = pageDataFromAtom?.filterTabs ?? [];
    const filterStates = pageDataFromAtom?.filterStates;
    const tilesAdminData = pageDataFromAtom?.tilesAdminData ?? [];

    const isLoadingFromProps = props.isLoading;

    const location = useLocation();
    const currentSearchParams = new URLSearchParams(location.search);
    const currentUrlSearchQuery = currentSearchParams.get('search') ?? '';

    const isMobile = appDisplayMode === 'mobile';
    const scrollToRef = useRef<HTMLDivElement | null>(null);
    const showMobileSearchResultsAll =
        isMobile &&
        filterTabs.find((tab: FilterTabViewModel) => tab.selected)?.id === 0;
    const setDisableSlider = useSetAtom(disableSliderAtom);
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    // Disables/enables a global slider based on whether mobile 'All' results view is active.
    useEffect(() => {
        setDisableSlider({
            disableSlider: true,
            showMobileSearchResultsAll,
        });
    }, [showMobileSearchResultsAll, setDisableSlider]);

    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const currentFilterStates = filterStates ?? { states: {} };
    const skeletonCount = isMobile ? 4 : 16;

    const [lastSearchQuery, setLastSearchQuery] = useState(
        currentUrlSearchQuery
    );

    const showFiltersHeader =
        lastSearchQuery === currentUrlSearchQuery || !isLoadingFromProps;

    useUpdateEffect(() => {
        if (currentUrlSearchQuery) {
            setLastSearchQuery(currentUrlSearchQuery);
        }
    }, [currentUrlSearchQuery]);

    const getAspectRatio = (model: TileableModel) => {
        return isArticleTileViewModel(model) ? 'article' : 'default';
    };

    const tiles: TileViewModel<TileableModel>[] = results.map(
        (result: TileableModel) => {
            const adminData = tilesAdminData.find(
                (tile: DirectoryItemAdmin) => tile.id === result.id
            );
            const adminStatusInfo = adminData
                ? mapDirectoryItemAdminToTile(adminData)
                : undefined;
            return {
                aspectRatio: getAspectRatio(result),
                model: {
                    ...result,
                    ...(isArticleTileViewModel(result) && {
                        variant: 'featured',
                    }),
                },
                adminStatusInfo,
            };
        }
    ) as TileViewModel<TileableModel>[];

    return (
        <div className="px-0 md:px-12" ref={scrollToRef}>
            {isLoadingFromProps ? (
                <>
                    <div className="px-6 md:hidden">
                        <Skeleton className="h-12 w-full" variant="light" />
                        <div className="mt-6 flex gap-2">
                            <Skeleton className="h-12 w-1/3" variant="light" />
                            <Skeleton className="h-12 w-1/3" variant="light" />
                            <Skeleton className="h-12 w-1/3" variant="light" />
                        </div>
                    </div>
                    <div className="hidden md:block">
                        {(
                            currentFilterStates.states.category as {
                                category?: { title: string };
                            }
                        )?.category?.title ? (
                            <div className="hidden md:block">
                                <div className="mt-6 flex gap-2">
                                    <Skeleton
                                        className="h-12 w-1/3"
                                        variant="light"
                                    />
                                    <Skeleton
                                        className="h-12 w-1/3"
                                        variant="light"
                                    />
                                    <Skeleton
                                        className="h-12 w-1/3"
                                        variant="light"
                                    />
                                </div>
                            </div>
                        ) : (
                            currentUrlSearchQuery && (
                                <>
                                    <div>
                                        <div className="mt-4 flex gap-4">
                                            {Array.from({ length: 4 }).map(
                                                (_, index) => (
                                                    <Skeleton
                                                        key={index}
                                                        className="h-30 w-68"
                                                        variant="light"
                                                    />
                                                )
                                            )}
                                        </div>
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </>
            ) : showFiltersHeader ? (
                <>
                    <SearchPageGridHeader isLoading={isLoadingFromProps} />
                    <SearchPageFilterTabs {...props} />
                </>
            ) : null}

            {showMobileSearchResultsAll ? (
                <MobileSearchResultsAll
                    {...props}
                    isLoading={isLoadingFromProps}
                />
            ) : isLoadingFromProps ? (
                <div className="px-6 pt-12 md:px-0">
                    <div className="hidden md:block">
                        {currentUrlSearchQuery ? (
                            <>
                                <Skeleton
                                    className="my-6 h-10 w-full"
                                    variant="light"
                                />
                                <div className="my-6 flex gap-4">
                                    {Array.from({ length: 4 }).map(
                                        (_, index) => (
                                            <Skeleton
                                                key={index}
                                                className="h-10 w-25"
                                                variant="light"
                                            />
                                        )
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="mb-16 flex gap-4">
                                {Array.from({ length: 2 }).map((_, index) => (
                                    <Skeleton
                                        key={index}
                                        className="h-10 w-40"
                                        variant="light"
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <Grid
                        colClasses="grid-cols-1 md:grid-cols-2 lg:grid-cols-4 qhd:grid-cols-5"
                        rowsGapClass="gap-y-12 md:gap-y-20"
                    >
                        {Array.from({ length: skeletonCount }).map(
                            (_, index) => (
                                <div
                                    key={index}
                                    className="aspect-tile-default"
                                >
                                    <Skeleton
                                        className="h-full w-full"
                                        variant="light"
                                    />
                                </div>
                            )
                        )}
                    </Grid>
                </div>
            ) : (
                <>
                    {tiles.length > 0 ? (
                        <>
                            <div className="px-6 pt-12 md:px-0">
                                <Grid
                                    ref={gridRef}
                                    colClasses="grid-cols-1 md:grid-cols-2 lg:grid-cols-4 qhd:grid-cols-5"
                                    rowsGapClass="gap-y-12 md:gap-y-20"
                                >
                                    {tiles.map((tile) => (
                                        <SearchPageTile
                                            key={tile.model.id}
                                            tile={{
                                                ...tile,
                                                isFollowed: isInDesignBoard(
                                                    tile.model.id
                                                ),
                                                onFollow: (id, type) =>
                                                    handleFollow(
                                                        {
                                                            id: id,
                                                        },
                                                        type
                                                    ),
                                            }}
                                        />
                                    ))}
                                </Grid>
                            </div>

                            <div className="py-20">
                                <TilePagination
                                    currentPath={location}
                                    current={pagination.page}
                                    total={pagination.totalPages}
                                    urlBuilder={buildPaginationUrl}
                                    scrollToRef={scrollToRef}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="px-6 md:px-0">
                            {pageDataFromAtom && (
                                <NoMatchesFound
                                    hideFilters={true}
                                    filterStates={currentFilterStates}
                                    onDisableFilter={props.handleDisableFilter}
                                    onResetFilter={
                                        props.searchFilters.resetFilters
                                    }
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
